import React, { useState } from 'react';
import { validatePhone, validateEmail } from '../../utils/validators';

const CustomerForm = ({ customer, onSubmit, onCancel, showCreditLimit = false }) => {
  const [formData, setFormData] = useState({
    name: customer?.name || '',
    phone: customer?.phone || '',
    email: customer?.email || '',
    area: customer?.address?.area || '',
    block: customer?.address?.block || '',
    building: customer?.address?.building || '',
    floor: customer?.address?.floor || '',
    apartment: customer?.address?.apartment || '',
    street: customer?.address?.street || '',
    city: customer?.address?.city || '',
    notes: customer?.notes || '',
    creditLimit: customer?.creditLimit ?? 0
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!validatePhone(formData.phone)) newErrors.phone = 'Valid 8-digit phone number required';
    if (formData.email && !validateEmail(formData.email)) newErrors.email = 'Valid email is required';
    if (showCreditLimit && formData.creditLimit < 0) newErrors.creditLimit = 'Credit limit cannot be negative';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const submitData = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || undefined,
        address: {
          area: formData.area,
          block: formData.block,
          building: formData.building,
          floor: formData.floor,
          apartment: formData.apartment,
          street: formData.street,
          city: formData.city
        },
        notes: formData.notes
      };
      if (showCreditLimit) submitData.creditLimit = parseFloat(formData.creditLimit) || 0;
      onSubmit(submitData);
    }
  };

  /* Helper: unified input styling matching PaymentSystem ps-modal-input */
  const inputStyle = (hasError) => ({
    width: '100%',
    border: `1.5px solid ${hasError ? '#ef4444' : '#e5e7eb'}`,
    borderRadius: 8,
    padding: '9px 13px',
    fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
    color: '#374151'
  });

  const labelStyle = {
    display: 'block',
    fontSize: 12,
    fontWeight: 700,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 5
  };

  const fieldStyle = { display: 'flex', flexDirection: 'column' };
  const sectionTitle = {
    fontSize: 11,
    fontWeight: 700,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    paddingTop: 4,
    borderTop: '1px solid #f3f4f6',
    marginTop: 4
  };

  const row = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* Basic Info */}
      <div style={sectionTitle}>Basic Information</div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Name *</label>
        <input
          name="name" value={formData.name} onChange={handleChange}
          style={inputStyle(errors.name)} placeholder="Full name" required
        />
        {errors.name && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 3 }}>{errors.name}</span>}
      </div>

      <div style={row}>
        <div style={fieldStyle}>
          <label style={labelStyle}>Phone * (8 digits)</label>
          <input
            name="phone" value={formData.phone} onChange={handleChange}
            style={inputStyle(errors.phone)} placeholder="e.g. 99887766"
          />
          {errors.phone && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 3 }}>{errors.phone}</span>}
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Email</label>
          <input
            name="email" type="email" value={formData.email} onChange={handleChange}
            style={inputStyle(errors.email)} placeholder="optional"
          />
          {errors.email && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 3 }}>{errors.email}</span>}
        </div>
      </div>

      {/* Kuwait Address */}
      <div style={sectionTitle}>Kuwait Address</div>

      <div style={row}>
        <div style={fieldStyle}>
          <label style={labelStyle}>Area</label>
          <input name="area" value={formData.area} onChange={handleChange} style={inputStyle()} placeholder="e.g. Salmiya" />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Block</label>
          <input name="block" value={formData.block} onChange={handleChange} style={inputStyle()} placeholder="e.g. 7" />
        </div>
      </div>

      <div style={row}>
        <div style={fieldStyle}>
          <label style={labelStyle}>Street</label>
          <input name="street" value={formData.street} onChange={handleChange} style={inputStyle()} />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Building</label>
          <input name="building" value={formData.building} onChange={handleChange} style={inputStyle()} />
        </div>
      </div>

      <div style={row}>
        <div style={fieldStyle}>
          <label style={labelStyle}>Floor</label>
          <input name="floor" value={formData.floor} onChange={handleChange} style={inputStyle()} />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Apartment</label>
          <input name="apartment" value={formData.apartment} onChange={handleChange} style={inputStyle()} />
        </div>
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>City</label>
        <input name="city" value={formData.city} onChange={handleChange} style={inputStyle()} />
      </div>

      {/* Credit Settings */}
      {showCreditLimit && (
        <>
          <div style={sectionTitle}>Credit Settings</div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Credit Limit (KD) — 0 disables credit</label>
            <input
              name="creditLimit" type="number" step="0.001" min="0"
              value={formData.creditLimit} onChange={handleChange}
              style={inputStyle(errors.creditLimit)} placeholder="0.000"
            />
            {errors.creditLimit && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 3 }}>{errors.creditLimit}</span>}
          </div>
        </>
      )}

      {/* Notes */}
      <div style={fieldStyle}>
        <label style={labelStyle}>Notes</label>
        <textarea
          name="notes" value={formData.notes} onChange={handleChange} rows={3}
          style={{ ...inputStyle(), resize: 'vertical', lineHeight: 1.5 }}
        />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
        <button
          type="button" onClick={onCancel}
          style={{
            padding: '9px 20px', border: '1.5px solid #e5e7eb', borderRadius: 8,
            background: '#f9fafb', fontSize: 14, fontWeight: 600, color: '#374151',
            cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s'
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          style={{
            padding: '9px 24px', border: 'none', borderRadius: 8,
            background: '#3b82f6', fontSize: 14, fontWeight: 700, color: '#fff',
            cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", transition: 'background 0.15s'
          }}
        >
          {customer ? 'Update' : 'Create'} Customer
        </button>
      </div>
    </form>
  );
};

export default CustomerForm;
